import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import { useMemo, useState, useEffect } from "react";
import { Node } from "@tiptap/core";
import Image from "@tiptap/extension-image";
import { Heading } from "@tiptap/extension-heading";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  Link as LinkIcon,
  FormatListBulleted,
  FormatListNumbered,
  Image as ImageIcon,
} from "@mui/icons-material";
import {
  Undo as UndoIcon,
  Redo as RedoIcon,
  LinkOff as UnlinkIcon,
} from "@mui/icons-material";

import {
  Box,
  CircularProgress,
  Dialog,
  Divider,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import Button, { ButtonProps } from "@mui/material/Button";
import { Stack, styled } from "@mui/system";
import { pageBuildingEndPointLogic } from "../../../../logic/AssetManagement/PageManagement - Copy/PageBuilding/pageBuildingEndPointLogic";
import { useSearchParams } from "react-router-dom";
import { selectUserToken } from "../../../../features/userSlice";
import { useSelector } from "react-redux";
import SimpleBackdrop from "../../../BackDrop/BackDrop";

// Define the shortcode array
const getShortCodeArray = (lang = "en") =>
  lang === "en"
    ? [
        {
          label: "YYYY",
          value: "<span class='sc_date'>[date-today format='Y']</span>",
          displayValue: "[date-today format='Y']",
        },
        {
          label: "Month YYYY",
          value: "<span class='sc_date'>[date-today format='F Y']</span>",
          displayValue: "[date-today format='F Y']",
        },
        {
          label: "Month DD, YYYY",
          value: "<span class='sc_date'>[date-today format='F j, Y']</span>",
          displayValue: "[date-today format='F j, Y']",
        },
        {
          label: "MMM YYYY",
          value: "<span class='sc_date'>[date-today format='M Y']</span>",
          displayValue: "[date-today format='M Y']",
        },
        {
          label: "MM/DD/YYYY",
          value: "<span class='sc_date'>[date-today format='m/d/Y']</span>",
          displayValue: "[date-today format='m/d/Y']",
        },
        {
          label: "DD/MM/YYYY",
          value: "<span class='sc_date'>[date-today format='d/m/Y']</span>",
          displayValue: "[date-today format='d/m/Y']",
        },
      ]
    : [
        {
          label: "YYYY",
          value:
            "<span class='sc_intl_date'>[intl-date-today format='Y']</span>",
          displayValue: "[intl-date-today format='Y']",
        },
        {
          label: "Month YYYY",
          value:
            "<span class='sc_intl_date'>[intl-date-today format='F Y']</span>",
          displayValue: "[intl-date-today format='F Y']",
        },
        {
          label: "Month DD, YYYY",
          value:
            "<span class='sc_intl_date'>[intl-date-today format='F j, Y']</span>",
          displayValue: "[intl-date-today format='F j, Y']",
        },
        {
          label: "MMM YYYY",
          value:
            "<span class='sc_intl_date'>[intl-date-today format='M Y']</span>",
          displayvalue: "[intl-date-today format='M Y']",
        },
        {
          label: "MM/DD/YYYY",
          value:
            "<span class='sc_intl_date'>[intl-date-today format='MM/dd/YYYY']</span>",
          displayValue: "[intl-date-today format='MM/dd/YYYY']",
        },
        {
          label: "DD/MM/YYYY",
          value:
            "<span class='sc_intl_date'>[intl-date-today format='dd/MM/YYYY']</span>",
          displayValue: "[intl-date-today format='dd/MM/YYYY']",
        },
      ];

const Shortcode = Node.create({
  name: "shortcode",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      value: { default: null },
      displayValue: { default: null },
      label: { default: null },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-type="shortcode"]',
        getAttrs: (dom) => {
          if (typeof dom === "string") return {};
          const element = dom as HTMLElement;
          return {
            value: element.getAttribute("data-value"),
            displayValue: element.textContent,
            label: element.getAttribute("data-label"),
          };
        },
      },
    ];
  },

  renderHTML({ node }) {
    return [
      "span",
      {
        "data-type": "shortcode",
        "data-value": node.attrs.value,
        "data-label": node.attrs.label,
        class: node.attrs.value.includes("sc_intl_date")
          ? "sc_intl_date"
          : "sc_date",
      },
      node.attrs.displayValue,
    ];
  },

  // This makes the node behave as a single unit for deletion
  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement("span");
      dom.textContent = node.attrs.displayValue;
      dom.setAttribute("data-type", "shortcode");
      dom.setAttribute("data-value", node.attrs.value);
      dom.setAttribute("data-label", node.attrs.label);
      dom.className = node.attrs.value.includes("sc_intl_date")
        ? "sc_intl_date"
        : "sc_date";

      return {
        dom,
        // This prevents the cursor from entering the node
        contentDOM: null,
      };
    };
  },
});

const MenuBar = styled(Box)({
  display: "flex",
  gap: "8px",
  flexWrap: "wrap",
  padding: "8px",
  borderBottom: "1px solid #e0e0e0",
  alignItems: "center",
  position: "sticky",
  top: 0,
  zIndex: 1000, // Increased z-index to ensure it stays above content
  backgroundColor: "white", // Added explicit background color
  boxShadow: "0 1px 3px rgba(0,0,0,0.1)", // Optional: adds subtle shadow for better visual separation
});

const ContentContainer = styled(Box)({
  flex: 1,
  overflowY: "auto",
  padding: "16px",
  maxHeight: "calc(100% - 120px)",
  "& .ProseMirror": {
    fontSize: "16px",
    lineHeight: "1.5",
    minHeight: "200px",
    "*": {
      color: "#000000 !important",
      backgroundColor: "white !important",
    },
    "p br": {
      display: "block !important", // Hide <br> tags inside <p> elements
    },
  },
  "& .ProseMirror p": {
    margin: "8px 0",
    minHeight: "1.2em",
  },
  "& .ProseMirror section, & .ProseMirror div": {
    display: "block",
    width: "100%",
  },
  "& .ProseMirror li": {
    position: "relative",
  },
  "& .ProseMirror-focused": {
    outline: "none",
  },
  "& .ProseMirror img": {
    maxWidth: "100%",
    height: "auto",
    maxHeight: "400px",
  },
  "& .ProseMirror li > p": {
    display: "inline",
    margin: 0,
  },
});

const ButtonContainer = styled(Box)({
  padding: "8px 16px",
  position: "sticky",
  bottom: 0,
  backgroundColor: "white",
  zIndex: 1,
});

const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      src: {
        default: null,
      },
      alt: {
        default: null,
      },
      title: {
        default: null,
      },
    };
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "img",
      {
        ...HTMLAttributes,
        style: "max-width: 100%; height: auto; max-height: 400px;",
      },
    ];
  },
  parseHTML() {
    return [
      {
        tag: "img[src]",
        getAttrs: (dom) => {
          if (typeof dom === "string") return {};
          const element = dom as HTMLImageElement;
          return {
            src: element.getAttribute("src"),
            alt: element.getAttribute("alt"),
            title: element.getAttribute("title"),
          };
        },
      },
    ];
  },
});

// Custom Heading extension that supports custom classes
const CustomHeading = Heading.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      class: {
        default: null,
        parseHTML: (element) => element.getAttribute("class"),
        renderHTML: (attributes) => {
          if (!attributes.class) {
            return {};
          }
          return {
            class: attributes.class,
          };
        },
      },
    };
  },
});

const ActiveButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "active",
})<ButtonProps & { active: boolean }>(({ active }) => ({
  fontWeight: active ? "bold" : "normal",
  backgroundColor: active ? "#f0f0f0" : "transparent",
  color: active ? "#000000DE" : "#666",
}));

const EditorContainer = styled(Box)({
  border: "1px solid #e0e0e0",
  borderRadius: "4px",
  marginBottom: "16px",
  display: "flex",
  flexDirection: "column",
  maxHeight: "500px", // Set a specific max height for the editor
  overflow: "hidden",
  minHeight: "300px",
  position: "relative", // Ensure proper stacking context
});

const TiptapEditor = ({
  initialContent = "",
  onSave,
  lang,
  enableShortcodes,
  useDictionaryHeaders,
  pageDictionary,
  onTempChange,
}: any) => {
  const [shortcodeMenuAnchorEl, setShortcodeMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const [imageMenuAnchorEl, setImageMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const isImageMenuOpen = Boolean(imageMenuAnchorEl);
  const isShortcodeMenuOpen = Boolean(shortcodeMenuAnchorEl);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [searchParams] = useSearchParams();
  const [uploading, setUploading] = useState(false);
  const [hasTextSelection, setHasTextSelection] = useState(false);
  const [hasLinkSelection, setHasLinkSelection] = useState(false);
  const siteUid = useMemo(
    () => searchParams.get("selectedSite"),
    [searchParams]
  ) as any;
  const oauthTokenHeader = useSelector(selectUserToken);
  const [imageUrlDialogOpen, setImageUrlDialogOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState("");

  const openShortcodeMenu = (event: React.MouseEvent<HTMLElement>) => {
    setShortcodeMenuAnchorEl(event.currentTarget);
  };

  const closeShortcodeMenu = () => {
    setShortcodeMenuAnchorEl(null);
  };

  // Add these state variables at the top of your component

  const handleImageMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setImageMenuAnchorEl(event.currentTarget);
  };

  const handleImageMenuClose = () => {
    setImageMenuAnchorEl(null);
  };

  const insertShortcode = (item: any) => {
    if (!editor) return;

    editor
      .chain()
      .focus()
      .insertContent({
        type: "shortcode",
        attrs: {
          value: item.value,
          displayValue: item.displayValue,
          label: item.label,
        },
      })
      .run();

    closeShortcodeMenu();
  };

  const handleInsertImageFromUrl = () => {
    if (!imageUrl || !editor) return;

    // Basic URL validation
    try {
      new URL(imageUrl);
      editor
        .chain()
        .focus()
        .setImage({
          src: imageUrl,
          style: "max-width: 100%; height: auto; max-height: 400px;",
        })
        .run();
      setImageUrlDialogOpen(false);
      setImageUrl("");
    } catch (e) {
      alert("Please enter a valid URL");
    }
  };

  const shortCodeArray = getShortCodeArray(lang);

  const jsonData = JSON.parse(pageDictionary);

  const getHeadingConfigs = () => {
    const headingConfigs: any[] = [
      {
        tag: "p",
        level: 0, // Using 0 to indicate paragraph
        class: null,
        label: "Normal Text",
        default_value: "",
      },
    ];

    jsonData.structure.forEach((section: any) => {
      if (section.content) {
        section.content.forEach((item: any) => {
          if (
            item.short_code &&
            (item.short_code.includes("date-today") ||
              item.short_code.includes("joli-toc"))
          ) {
            return;
          }
          if (item.tag && item.tag.match(/^h[1-6]$/)) {
            headingConfigs.push({
              tag: item.tag,
              level: parseInt(item.tag.substring(1)),
              class: item.class || null,
              label:
                item.label ||
                `${item.tag.toUpperCase()}${
                  item.class ? ` (${item.class})` : ""
                }`,
              default_value: item.default_value || "PUT content here",
            });
          }
        });
      }
    });

    return headingConfigs;
  };

  const headingConfigs = useDictionaryHeaders && getHeadingConfigs();
  const isValidUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch (e) {
      return false;
    }
  };

  const extensions = [
    StarterKit.configure({
      heading: false,
      paragraph: true as any,
    }),
    CustomHeading.configure({
      levels: [1, 2, 3, 4, 5, 6],
    }),
    Underline,
    Link.configure({
      openOnClick: false,
      autolink: false,
      linkOnPaste: false,
      HTMLAttributes: {
        target: "_blank",
        rel: "noopener",
      },
    }),
    CustomImage,
    ...(enableShortcodes ? [Shortcode] : []),
  ];

  const editor = useEditor({
    extensions,
    content: initialContent,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onTempChange?.(html); // Send the latest text up
    },
  }) as any;

  // Track text selection for link button
  useEffect(() => {
    if (!editor) return;

    const updateSelection = () => {
      const { from, to } = editor.state.selection;
      const hasSelection = from !== to;
      setHasTextSelection(hasSelection);

      // Check if selection contains a link
      const linkMark = editor.state.schema.marks.link;
      const hasLink =
        hasSelection && editor.state.doc.rangeHasMark(from, to, linkMark);
      setHasLinkSelection(hasLink);
    };

    editor.on("selectionUpdate", updateSelection);
    editor.on("transaction", updateSelection);

    return () => {
      editor.off("selectionUpdate", updateSelection);
      editor.off("transaction", updateSelection);
    };
  }, [editor]);

  const setLink = () => {
    if (!editor || !hasTextSelection) return;

    const previousUrl = editor.getAttributes("link").href || "";
    setLinkUrl(previousUrl);
    setLinkDialogOpen(true);
  };

  const handleConfirmLink = () => {
    let processedUrl = linkUrl.trim();

    if (processedUrl) {
      // If it's an email address (basic check)
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(processedUrl)) {
        processedUrl = `${processedUrl}`;
      }
      // If it's not email and doesn't start with http/https, add https://
      else if (!/^https?:\/\//i.test(processedUrl)) {
        processedUrl = `https://${processedUrl}`;
      }
    }

    if (processedUrl === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: processedUrl })
        .run();
    }
    setLinkDialogOpen(false);
    setLinkUrl("");
  };

  const handleSave = () => {
    if (!editor) return;

    let html = editor.getHTML();

    // Clean up <li><p>...</p></li> into <li>...</li>
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    doc.querySelectorAll("li > p").forEach((p) => {
      const parent = p.parentElement;
      if (parent && parent.tagName.toLowerCase() === "li") {
        // Replace <li><p>text</p></li> with <li>text</li>
        const span = document.createElement("span");
        span.innerHTML = p.innerHTML;
        parent.replaceChild(span, p);
      }
    });

    html = doc.body.innerHTML;

    onSave(html);
  };

  const getActiveHeadingLabel = () => {
    if (!editor) return "";

    // Check for paragraph first
    if (editor.isActive("paragraph")) {
      return "Normal Text";
    }

    // Check for headings
    for (const item of headingConfigs) {
      if (
        item.tag !== "p" &&
        editor.isActive("heading", { level: item.level })
      ) {
        const currentClass = editor.getAttributes("heading").class;
        if (currentClass === item.class) {
          return item.label;
        }
      }
    }

    return "";
  };

  // Function to toggle heading with custom class
  const toggleCustomHeading = (level: number, className?: string) => {
    if (!editor) return;

    if (level === 0) {
      editor.chain().focus().setParagraph().run();
      return;
    }

    editor.chain().focus().setHeading({ level }).run();
    if (className) {
      editor.commands.updateAttributes("heading", { class: className });
    } else {
      editor.commands.updateAttributes("heading", { class: null });
    }
  };

  // Function to check if a heading with specific class is active

  if (!editor) {
    return null;
  }

  const handleCancel = () => {
    if (editor) {
      editor.commands.setContent(initialContent);
    }
    onSave(initialContent); // This assumes onSave also handles closing
  };

  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const uploadImageAPI = async (
    base64: string,
    fileName: string
  ): Promise<string> => {
    try {
      const payload = { data: { base64_image: base64, file_name: fileName } };

      const result = await pageBuildingEndPointLogic.imageUpload(
        siteUid,
        oauthTokenHeader,
        payload,
        () => {}
      );
      handleImageMenuClose();

      if (!result) {
        throw new Error("No response from image upload API");
      }

      return result;
    } catch (error) {
      console.error("Image upload failed:", error);
      throw error; // Re-throw to let the caller handle it
    }
  };

  return (
    <>
      {uploading && <SimpleBackdrop />}
      <EditorContainer>
        <MenuBar sx={{ padding: "4px 8px", gap: "2px", minHeight: "auto" }}>
          {/* Undo/Redo Group */}
          <Box sx={{ display: "flex", gap: "1px", marginRight: "8px" }}>
            <ActiveButton
              onClick={() => editor.chain().focus().undo().run()}
              disabled={!editor.can().undo()}
              active={false}
              size="small"
              sx={{ minWidth: "32px", padding: "4px" }}
            >
              <Tooltip title="Undo" placement="top" arrow>
                <UndoIcon fontSize="small" />
              </Tooltip>
            </ActiveButton>

            <ActiveButton
              onClick={() => editor.chain().focus().redo().run()}
              disabled={!editor.can().redo()}
              active={false}
              size="small"
              sx={{ minWidth: "32px", padding: "4px" }}
            >
              <Tooltip title="Redo" placement="top" arrow>
                <RedoIcon fontSize="small" />
              </Tooltip>
            </ActiveButton>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ margin: "0 4px" }} />

          {/* Text Formatting Group */}
          <Box sx={{ display: "flex", gap: "1px", marginRight: "8px" }}>
            <ActiveButton
              onClick={() => editor.chain().focus().toggleBold().run()}
              active={editor.isActive("bold")}
              size="small"
              sx={{ minWidth: "32px", padding: "4px" }}
            >
              <Tooltip title="Bold (Ctrl+B)" placement="top" arrow>
                <FormatBold fontSize="small" />
              </Tooltip>
            </ActiveButton>

            <ActiveButton
              onClick={() => editor.chain().focus().toggleItalic().run()}
              active={editor.isActive("italic")}
              size="small"
              sx={{ minWidth: "32px", padding: "4px" }}
            >
              <Tooltip title="Italic (Ctrl+I)" placement="top" arrow>
                <FormatItalic fontSize="small" />
              </Tooltip>
            </ActiveButton>

            <ActiveButton
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              active={editor.isActive("underline")}
              size="small"
              sx={{ minWidth: "32px", padding: "4px" }}
            >
              <Tooltip title="Underline (Ctrl+U)" placement="top" arrow>
                <FormatUnderlined fontSize="small" />
              </Tooltip>
            </ActiveButton>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ margin: "0 4px" }} />

          {/* Link Group */}
          <Box sx={{ display: "flex", gap: "1px", marginRight: "8px" }}>
            <ActiveButton
              onClick={setLink}
              active={editor.isActive("link")}
              disabled={!hasTextSelection}
              size="small"
              sx={{
                minWidth: "32px",
                padding: "4px",
                opacity: hasTextSelection ? 1 : 0.4,
              }}
            >
              <Tooltip
                title={
                  hasTextSelection
                    ? "Insert Link or Email"
                    : "Select text first"
                }
                placement="top"
                arrow
              >
                <LinkIcon fontSize="small" />
              </Tooltip>
            </ActiveButton>

            <ActiveButton
              onClick={() => editor.chain().focus().unsetLink().run()}
              disabled={!hasLinkSelection}
              active={false}
              size="small"
              sx={{
                minWidth: "32px",
                padding: "4px",
                opacity: hasLinkSelection ? 1 : 0.4,
              }}
            >
              <Tooltip title="Remove Link" placement="top" arrow>
                <UnlinkIcon fontSize="small" />
              </Tooltip>
            </ActiveButton>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ margin: "0 4px" }} />

          {/* Heading Dropdown */}
          <FormControl size="small" sx={{ minWidth: 140, marginRight: "8px" }}>
            <Select
              value={getActiveHeadingLabel()}
              onChange={(e) => {
                const selectedLabel = e.target.value;
                const selectedItem = headingConfigs.find(
                  (item: any) => item.label === selectedLabel
                );
                if (selectedItem) {
                  toggleCustomHeading(selectedItem.level, selectedItem.class);
                }
              }}
              color="secondary"
              displayEmpty
              variant="outlined"
              sx={{
                height: "32px",
                fontSize: "0.875rem",
                "& .MuiSelect-select": {
                  padding: "4px 8px",
                },
              }}
            >
              {headingConfigs.map((item: any, index: number) => (
                <MenuItem
                  key={index}
                  value={item.label}
                  sx={{ fontSize: "0.875rem" }}
                >
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider orientation="vertical" flexItem sx={{ margin: "0 4px" }} />

          {/* Lists Group */}
          <Box sx={{ display: "flex", gap: "1px", marginRight: "8px" }}>
            <ActiveButton
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              active={editor.isActive("bulletList")}
              size="small"
              sx={{ minWidth: "32px", padding: "4px" }}
            >
              <Tooltip title="Bullet List" placement="top" arrow>
                <FormatListBulleted fontSize="small" />
              </Tooltip>
            </ActiveButton>

            <ActiveButton
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              active={editor.isActive("orderedList")}
              size="small"
              sx={{ minWidth: "32px", padding: "4px" }}
            >
              <Tooltip title="Numbered List" placement="top" arrow>
                <FormatListNumbered fontSize="small" />
              </Tooltip>
            </ActiveButton>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ margin: "0 4px" }} />

          {/* Media Group */}
          <Box sx={{ display: "flex", gap: "1px", marginRight: "8px" }}>
            <ActiveButton
              component="span"
              active={false}
              size="small"
              sx={{ minWidth: "32px", padding: "4px" }}
            >
              <Tooltip title="Insert Image" placement="top" arrow>
                <IconButton
                  onClick={handleImageMenuClick}
                  size="small"
                  sx={{ padding: "2px" }}
                  disabled={uploading}
                >
                  {uploading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <ImageIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>

              <Menu
                anchorEl={imageMenuAnchorEl}
                open={isImageMenuOpen}
                onClose={handleImageMenuClose}
                PaperProps={{
                  sx: { minWidth: 180 },
                }}
              >
                <MenuItem
                  onClick={() => {
                    handleImageMenuClose();
                    setImageUrlDialogOpen(true);
                  }}
                  sx={{ fontSize: "0.875rem", padding: "8px 16px" }}
                >
                  <LinkIcon fontSize="small" sx={{ marginRight: "8px" }} />
                  Insert from URL
                </MenuItem>
                <MenuItem
                  component="label"
                  sx={{ fontSize: "0.875rem", padding: "8px 16px" }}
                >
                  <FileUploadIcon
                    fontSize="small"
                    sx={{ marginRight: "8px" }}
                  />
                  Upload from computer
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file || !editor) {
                        if (e.target) e.target.value = "";
                        return;
                      }

                      if (!file.type.startsWith("image/")) {
                        alert("Please select a valid image file");
                        if (e.target) e.target.value = "";
                        return;
                      }

                      const MAX_SIZE = 5 * 1024 * 1024;
                      if (file.size > MAX_SIZE) {
                        alert("Image size must be less than 5MB");
                        if (e.target) e.target.value = "";
                        return;
                      }

                      try {
                        handleImageMenuClose();
                        setUploading(true);

                        const base64 = await convertToBase64(file);
                        const imageUrl = (await uploadImageAPI(
                          base64,
                          file.name
                        )) as any;

                        editor
                          .chain()
                          .focus()
                          .setImage({
                            src: imageUrl?.upload_path,
                            alt: file.name,
                            title: file.name,
                            style:
                              "max-width: 100%; height: auto; max-height: 400px;",
                          })
                          .run();

                        if (base64.startsWith("data:")) {
                          URL.revokeObjectURL(base64);
                        }
                      } finally {
                        setUploading(false);
                        if (e.target) e.target.value = "";
                      }
                    }}
                  />
                </MenuItem>
              </Menu>
            </ActiveButton>
          </Box>

          {enableShortcodes && (
            <>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ margin: "0 4px" }}
              />

              <ActiveButton
                onClick={openShortcodeMenu}
                active={false}
                size="small"
                sx={{ minWidth: "32px", padding: "4px" }}
              >
                <Tooltip title="Insert Date Shortcode" placement="top" arrow>
                  <CalendarTodayIcon fontSize="small" />
                </Tooltip>
              </ActiveButton>

              <Menu
                anchorEl={shortcodeMenuAnchorEl}
                open={isShortcodeMenuOpen}
                onClose={closeShortcodeMenu}
                PaperProps={{
                  sx: { minWidth: 160 },
                }}
              >
                {shortCodeArray.map((item, index) => (
                  <MenuItem
                    key={index}
                    onClick={() => insertShortcode(item)}
                    sx={{ fontSize: "0.875rem", padding: "8px 16px" }}
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </MenuBar>
        <ContentContainer>
          <EditorContent editor={editor} />
        </ContentContainer>
        <ButtonContainer>
          <Stack
            flexDirection={"row"}
            justifyContent={"end"}
            gap={1}
            marginTop={1}
          >
            <Button
              onClick={handleCancel}
              variant="outlined"
              color="secondary"
              endIcon={<CloseIcon />}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              color="secondary"
              endIcon={<SaveIcon />}
            >
              Save
            </Button>
          </Stack>
        </ButtonContainer>
        {/* Link Dialog */}
        <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)}>
          <Box p={2} width={500}>
            <Typography variant="h6">Insert Link or Email</Typography>
            <TextField
              autoFocus
              margin="dense"
              label="URL"
              fullWidth
              variant="outlined"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              color="secondary"
              size="small"
              placeholder="https://example.com or mailto:nIq5b@example.com"
              error={
                linkUrl.trim() !== "" &&
                !isValidUrl(
                  linkUrl.trim().match(/^https?:\/\//)
                    ? linkUrl.trim()
                    : `https://${linkUrl.trim()}`
                )
              }
              helperText={
                linkUrl.trim() !== "" &&
                !isValidUrl(
                  linkUrl.trim().match(/^https?:\/\//)
                    ? linkUrl.trim()
                    : `https://${linkUrl.trim()}`
                )
                  ? "Please enter a valid URL"
                  : "Include https:// for external links and mailto: for emails"
              }
            />
            <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
              <Button
                onClick={() => setLinkDialogOpen(false)}
                color="secondary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmLink}
                variant="contained"
                color="secondary"
                disabled={
                  linkUrl.trim() !== "" &&
                  !isValidUrl(
                    linkUrl.trim().match(/^https?:\/\//)
                      ? linkUrl.trim()
                      : `https://${linkUrl.trim()}`
                  )
                }
              >
                Insert
              </Button>
            </Box>
          </Box>
        </Dialog>
        {/* Image URL Dialog */}
        <Dialog
          open={imageUrlDialogOpen}
          onClose={() => setImageUrlDialogOpen(false)}
        >
          <Box p={2} width={400}>
            <Typography variant="h6">Insert Image from URL</Typography>
            <TextField
              autoFocus
              margin="dense"
              label="Image URL"
              fullWidth
              variant="outlined"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              color="secondary"
              size="small"
              placeholder="https://example.com/image.jpg"
              error={imageUrl.trim() !== "" && !isValidUrl(imageUrl.trim())}
              helperText={
                imageUrl.trim() !== "" && !isValidUrl(imageUrl.trim())
                  ? "Please enter a valid URL"
                  : "Include https:// for external images"
              }
            />
            <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
              <Button
                onClick={() => setImageUrlDialogOpen(false)}
                color="secondary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleInsertImageFromUrl}
                variant="contained"
                color="secondary"
                disabled={!imageUrl.trim() || !isValidUrl(imageUrl.trim())}
              >
                Insert
              </Button>
            </Box>
          </Box>
        </Dialog>
      </EditorContainer>
    </>
  );
};

export default TiptapEditor;
